'use client';

import { c as _c } from "react/compiler-runtime";
import './cart-drawer.scss';
import type { ReactElement } from 'react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Divider } from '@sola/ark-core/atoms/divider/divider.tsx';
import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { Sizes, Text, Variants } from '@sola/ark-core/atoms/text/text.tsx';
import { Alert, type AlertProps } from '@sola/ark-core/molecules/alert/alert.tsx';
import { Drawer, DrawerActions, DrawerBody, DrawerClose, DrawerContent, DrawerFooter, type DrawerProps, DrawerTrigger } from '@sola/ark-core/molecules/drawer/drawer.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { useCart } from '@sola/cart-sdk/store.tsx';
import { getPublicConfig } from '@sola/config/lib/getters.ts';
import type { CartItem } from '@sola/schema/dotcom/cart/cart-item.ts';
import { knownSkippyUrls } from '@sola/skippy-sdk/urls.ts';
import { CartItem as CartItemComponent } from '../cart-item/cart-item.tsx';
import { MiniGallery } from '../mini-gallery/mini-gallery.tsx';
const cartDrawerFocusTargetAttribute = 'data-cart-drawer-focus-target';
export const cartDrawerFocusTargetProps = {
  [cartDrawerFocusTargetAttribute]: true
} as const;
const restoreCartDrawerFocus = (event: Pick<Event, 'preventDefault'>) => {
  const returnFocusTarget = globalThis.document.querySelector<HTMLElement>(`[${cartDrawerFocusTargetAttribute}]`);
  if (!returnFocusTarget?.isConnected) {
    return;
  }
  returnFocusTarget.focus();
  if (returnFocusTarget.ownerDocument.activeElement === returnFocusTarget) {
    event.preventDefault();
  }
};
export interface CartDrawerAlert {
  message: AlertProps['message'];
  variant?: AlertProps['variant'];
  title?: AlertProps['title'];
}
type CartDrawerProps = Omit<DrawerProps, 'children'> & {
  trigger?: ReactElement;
  alert?: CartDrawerAlert | null;
  onAlertClose?: () => void;
};
interface CartDrawerBodyProps {
  cartItems: CartItem[] | undefined;
  hasError: boolean;
  isReady: boolean;
}
interface CartDrawerSubtitleProps {
  isReady: boolean;
  itemCount: number;
  totalToday: number;
}
interface CartDrawerListProps {
  cartItems: CartItem[];
}
const CartDrawerEmpty: React.FC = () => {
  const $ = _c(48);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["cart", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  let T0;
  let T1;
  let t1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[1] !== t) {
    const {
      NEXT_PUBLIC_BASE_URL_DOTCOM: dotcomBaseUrl
    } = getPublicConfig();
    t11 = "CartDrawer__empty py-comfy-lg overflow-hidden";
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <MiniGallery className="pl-comfy-sm md:pl-comfy-xl" />;
      $[17] = t12;
    } else {
      t12 = $[17];
    }
    t8 = "px-comfy-sm md:px-comfy-xl";
    const t14 = t("cart:emptyCart.title");
    if ($[18] !== t14) {
      t9 = <Text asChild={true} variant={Variants.Heading} size={Sizes.XSmall}><p className="mt-comfy-xl">{t14}</p></Text>;
      $[18] = t14;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    const t15 = t("cart:emptyCart.subTitle");
    if ($[20] !== t15) {
      t10 = <p>{t15}</p>;
      $[20] = t15;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    t6 = "mt-comfy-xl flex flex-col gap-comfy-sm md:flex-row";
    let t16;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <ArkIcon iconName="arrow-right" />;
      $[22] = t16;
    } else {
      t16 = $[22];
    }
    t7 = <ButtonStyle variant="primary" size="md" iconAfter={t16} fullWidth={true}><Link href={new URL(knownSkippyUrls.sac, dotcomBaseUrl)}>{t("core:actions.sponsorAChild")}</Link></ButtonStyle>;
    T1 = ButtonStyle;
    t2 = "secondary";
    t3 = "md";
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <ArkIcon iconName="arrow-right" />;
      $[23] = t4;
    } else {
      t4 = $[23];
    }
    t5 = true;
    T0 = Link;
    t1 = new URL(knownSkippyUrls.give, dotcomBaseUrl);
    t13 = t("core:actions.donate");
    $[1] = t;
    $[2] = T0;
    $[3] = T1;
    $[4] = t1;
    $[5] = t10;
    $[6] = t11;
    $[7] = t12;
    $[8] = t13;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    T0 = $[2];
    T1 = $[3];
    t1 = $[4];
    t10 = $[5];
    t11 = $[6];
    t12 = $[7];
    t13 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
    t8 = $[15];
    t9 = $[16];
  }
  let t14;
  if ($[24] !== T0 || $[25] !== t1 || $[26] !== t13) {
    t14 = <T0 href={t1}>{t13}</T0>;
    $[24] = T0;
    $[25] = t1;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== T1 || $[29] !== t14 || $[30] !== t2 || $[31] !== t3 || $[32] !== t4 || $[33] !== t5) {
    t15 = <T1 variant={t2} size={t3} iconAfter={t4} fullWidth={t5}>{t14}</T1>;
    $[28] = T1;
    $[29] = t14;
    $[30] = t2;
    $[31] = t3;
    $[32] = t4;
    $[33] = t5;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t15 || $[36] !== t6 || $[37] !== t7) {
    t16 = <div className={t6}>{t7}{t15}</div>;
    $[35] = t15;
    $[36] = t6;
    $[37] = t7;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t10 || $[40] !== t16 || $[41] !== t8 || $[42] !== t9) {
    t17 = <div className={t8}>{t9}{t10}{t16}</div>;
    $[39] = t10;
    $[40] = t16;
    $[41] = t8;
    $[42] = t9;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== t11 || $[45] !== t12 || $[46] !== t17) {
    t18 = <div className={t11}>{t12}{t17}</div>;
    $[44] = t11;
    $[45] = t12;
    $[46] = t17;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  return t18;
};
const CartDrawerList: React.FC<CartDrawerListProps> = t0 => {
  const $ = _c(4);
  const {
    cartItems
  } = t0;
  let t1;
  if ($[0] !== cartItems) {
    t1 = cartItems.map(_temp);
    $[0] = cartItems;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="CartDrawer__list px-comfy-sm md:px-comfy-xl pb-comfy-lg">{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
const CartDrawerBody: React.FC<CartDrawerBodyProps> = t0 => {
  const $ = _c(5);
  const {
    cartItems,
    hasError,
    isReady
  } = t0;
  if (!isReady) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="CartDrawer__loading my-comfy-sm px-comfy-sm md:px-comfy-xl"><Text asChild={true} variant={Variants.Label}><Skeleton count={3} height="4rem" className="my-snug-xl" /></Text></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (hasError) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="px-comfy-sm md:px-comfy-xl"><Alert className="my-comfy-md" variant="negative" message={<IntlBlock i18nKey="cart:errors.generic" components={{
          anchor: <a />
        }} />} /></div>;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (!cartItems?.length) {
    let t1;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <CartDrawerEmpty />;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  if ($[3] !== cartItems) {
    t1 = <CartDrawerList cartItems={cartItems} />;
    $[3] = cartItems;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
const CartDrawerSubtitle: React.FC<CartDrawerSubtitleProps> = t0 => {
  const $ = _c(13);
  const {
    isReady,
    itemCount,
    totalToday
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["cart", "core"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  if (!isReady) {
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Skeleton height="1.125rem" width="7.5rem" />;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  let t2;
  if ($[2] !== t || $[3] !== totalToday) {
    t2 = t("core:numbers.currencyCustom", {
      amount: totalToday
    });
    $[2] = t;
    $[3] = totalToday;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== itemCount || $[6] !== t) {
    t3 = t("cart:drawer.itemCount", {
      count: itemCount
    });
    $[5] = itemCount;
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t3) {
    t4 = <Text asChild={true} variant={Variants.Label}><span className="label-neutral-mild">{t3}</span></Text>;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t4) {
    t5 = <>{t2}{" "}{t4}</>;
    $[10] = t2;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
};
export const CartDrawer: React.FC<CartDrawerProps> = t0 => {
  const $ = _c(49);
  let alert;
  let drawerProps;
  let onAlertClose;
  let trigger;
  if ($[0] !== t0) {
    ({
      trigger,
      alert,
      onAlertClose,
      ...drawerProps
    } = t0);
    $[0] = t0;
    $[1] = alert;
    $[2] = drawerProps;
    $[3] = onAlertClose;
    $[4] = trigger;
  } else {
    alert = $[1];
    drawerProps = $[2];
    onAlertClose = $[3];
    trigger = $[4];
  }
  let t1;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["cart", "core"];
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const {
    t
  } = useTranslation(t1);
  const {
    actions: t2,
    cart,
    errors,
    isReady
  } = useCart("actions", "cart", "errors", "isReady");
  const {
    goToCheckout
  } = t2;
  const itemCount = cart?.items?.length ?? 0;
  const totalToday = cart?.totalToday ?? 0;
  const error = errors?.[0];
  let t3;
  if ($[6] !== trigger) {
    t3 = trigger && <DrawerTrigger>{trigger}</DrawerTrigger>;
    $[6] = trigger;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = trigger ? undefined : restoreCartDrawerFocus;
  let t5;
  if ($[8] !== t) {
    t5 = t("cart:drawer.title");
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== isReady || $[11] !== itemCount || $[12] !== totalToday) {
    t6 = <CartDrawerSubtitle isReady={isReady} itemCount={itemCount} totalToday={totalToday} />;
    $[10] = isReady;
    $[11] = itemCount;
    $[12] = totalToday;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== alert || $[15] !== onAlertClose) {
    t7 = alert && <div className="px-comfy-sm md:px-comfy-xl"><Alert className="my-comfy-md" variant={alert.variant ?? "positive"} title={alert.title} message={alert.message} closable={true} onClose={onAlertClose} /></div>;
    $[14] = alert;
    $[15] = onAlertClose;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = cart?.items;
  const t9 = !!error;
  let t10;
  if ($[17] !== isReady || $[18] !== t8 || $[19] !== t9) {
    t10 = <CartDrawerBody cartItems={t8} hasError={t9} isReady={isReady} />;
    $[17] = isReady;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t7) {
    t11 = <DrawerBody>{t7}{t10}</DrawerBody>;
    $[21] = t10;
    $[22] = t7;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t) {
    t12 = t("core:actions.close");
    $[24] = t;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12) {
    t13 = <DrawerClose asChild={true}><ButtonStyle fullWidth={true} purpose="neutral" variant="secondary"><button type="button">{t12}</button></ButtonStyle></DrawerClose>;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const t14 = !cart?.items?.length;
  let t15;
  if ($[28] !== t) {
    t15 = t("cart:drawer.checkoutLabel");
    $[28] = t;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== goToCheckout || $[31] !== t15) {
    t16 = <button onClick={goToCheckout}>{t15}</button>;
    $[30] = goToCheckout;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t14 || $[34] !== t16) {
    t17 = <ButtonStyle fullWidth={true} disabled={t14}>{t16}</ButtonStyle>;
    $[33] = t14;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t13 || $[37] !== t17) {
    t18 = <DrawerFooter><DrawerActions>{t13}{t17}</DrawerActions></DrawerFooter>;
    $[36] = t13;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t11 || $[40] !== t18 || $[41] !== t4 || $[42] !== t5 || $[43] !== t6) {
    t19 = <DrawerContent className="CartDrawer__content" onCloseAutoFocus={t4} title={t5} subtitle={t6}>{t11}{t18}</DrawerContent>;
    $[39] = t11;
    $[40] = t18;
    $[41] = t4;
    $[42] = t5;
    $[43] = t6;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== drawerProps || $[46] !== t19 || $[47] !== t3) {
    t20 = <Drawer {...drawerProps}>{t3}{t19}</Drawer>;
    $[45] = drawerProps;
    $[46] = t19;
    $[47] = t3;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  return t20;
};
function _temp(item) {
  return <Fragment key={item.id}><div className="CartDrawer__item my-comfy-md"><CartItemComponent item={item} /></div><Divider purpose="neutral" /></Fragment>;
}